/** Runtime configuration: `window.SOLAROS_PLATFORM` (written by docker-entrypoint.sh) over the build-time VITE_*
 * variables. Empty `apiUrl` = same origin (the Vite dev proxy, or a reverse proxy in front of the app). */
interface RuntimeConfig {
  apiUrl?: string;
  authAuthority?: string;
  authClientId?: string;
  authScope?: string;
  authMode?: string;
  wordpressPluginUrl?: string;
  /** Google Maps Platform browser key (Map Tiles API) for the design editor's 3D view; restricted to the
   * dashboard's domains. Empty = the 3D view shows the design on its own imagery. */
  mapsBrowserKey?: string;
  identityUrl?: string;
  /** The dashboard's app registration in the SSO user flow (company sign-in); empty = the one discovery names. */
  ssoClientId?: string;
  /** The marketing site (the SolarOS logo links to it); empty = https://solar-os.republic.global. */
  landingUrl?: string;
}

declare global {
  interface Window {
    SOLAROS_PLATFORM?: RuntimeConfig;
  }
}

const runtime: RuntimeConfig = (typeof window !== "undefined" && window.SOLAROS_PLATFORM) || {};
const env = import.meta.env;

function pick(runtimeValue: string | undefined, buildValue: string | undefined, fallback = ""): string {
  if (runtimeValue !== undefined && runtimeValue !== "") return runtimeValue;
  return buildValue ?? fallback;
}

const authority = pick(runtime.authAuthority, env.VITE_AUTH_AUTHORITY);

export const config = {
  apiUrl: pick(runtime.apiUrl, env.VITE_API_URL).replace(/\/+$/, ""),
  authAuthority: authority,
  authClientId: pick(runtime.authClientId, env.VITE_AUTH_CLIENT_ID),
  /** Extra scopes for the API access token, e.g. `api://<api app id>/access_as_user`. */
  authScope: pick(runtime.authScope, env.VITE_AUTH_SCOPE),
  /** `oidc` when an authority is configured, else `dev` (the backend's AUTH_MODE=dev headers). */
  authMode: (pick(runtime.authMode, env.VITE_AUTH_MODE) || (authority ? "oidc" : "dev")) as "oidc" | "dev",
  wordpressPluginUrl: pick(runtime.wordpressPluginUrl, env.VITE_WORDPRESS_PLUGIN_URL),
  mapsBrowserKey: pick(runtime.mapsBrowserKey, env.VITE_GOOGLE_MAPS_BROWSER_KEY),
  /** The identity service (company SSO, apps/identity-service); empty = SSO pages and discovery are off. */
  identityUrl: pick(runtime.identityUrl, env.VITE_IDENTITY_URL).replace(/\/+$/, ""),
  /** Company SSO signs in through this app (lib/ssoClient.ts); empty = the client id in discovery's login_url. */
  ssoClientId: pick(runtime.ssoClientId, env.VITE_SSO_CLIENT_ID),
  landingUrl: pick(runtime.landingUrl, env.VITE_LANDING_URL).replace(/\/+$/, ""),
};

/** The marketing site the SolarOS logo opens (same tab): the configured one, else the one environment's,
 * solar-os.republic.global (like the plugin link below). */
export function landingUrl(): string {
  return config.landingUrl || "https://solar-os.republic.global";
}

/** The Help Centre for installers (apps/help), served by the marketing site under /help/; `article` is a page path
 * such as "lux-and-billing/" (empty: the home page). */
export function helpUrl(article = ""): string {
  return `${landingUrl()}/help/${article.replace(/^\/+/, "")}`;
}

/** Where the WordPress plugin zip is published (deploy-wordpress-plugin.yml): the configured link, else the one
 * release site, plugin-solar-os (Solar OS runs one environment). */
export function wordpressPluginUrl(): string {
  return config.wordpressPluginUrl || "https://plugin-solar-os.republic.global/solar-quote-pro-latest.zip";
}

/** The API origin as the widget and snippets must name it (absolute, even when the app uses a proxy). */
export function publicApiOrigin(): string {
  return config.apiUrl || window.location.origin;
}
